import React from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentTypeConfig, formatMoney, formatDate, isOverdue, getDaysUntilDue } from './accountingConstants';

export default function PaymentsTab({
  allInvoices,
  markingPaid,
  markAsPaid
}) {
  const navigate = useNavigate();

  // Grupuj faktury po sposobie platnosci
  const safeInvoices = Array.isArray(allInvoices) ? allInvoices : [];
  const byPaymentType = {};

  safeInvoices.forEach(inv => {
    if (inv.status === 'odrzucona') return;

    const type = inv.sposob_platnosci || 'nieokreslony';
    if (!byPaymentType[type]) {
      byPaymentType[type] = { invoices: [], total: 0, paid: 0, unpaid: 0, corrections: 0 };
    }
    byPaymentType[type].invoices.push(inv);

    const brutto = parseFloat(inv.suma_brutto) || 0;

    if (inv.typ_faktury === 'korekta' || brutto < 0) {
      byPaymentType[type].corrections += brutto;
      byPaymentType[type].total += brutto;
    } else {
      byPaymentType[type].total += brutto;
      if (inv.status === 'zatwierdzona') {
        byPaymentType[type].paid += brutto;
      } else {
        byPaymentType[type].unpaid += brutto;
      }
    }
  });

  return (
    <div>
      {/* Podsumowanie po typach platnosci */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        {Object.entries(byPaymentType).map(([type, data]) => {
          const config = paymentTypeConfig[type] || { icon: '💲', label: type, color: 'var(--text-muted)' };
          return (
            <div key={type} style={{
              backgroundColor: "var(--card-bg)",
              borderRadius: '6px',
              border: '1px solid var(--border)',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '0.75rem 1rem',
                backgroundColor: config.color + '15',
                borderBottom: `2px solid ${config.color}`,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '18px' }}>{config.icon}</span>
                <span style={{ fontWeight: 600, fontSize: '13px', color: config.color }}>{config.label}</span>
                <span style={{
                  marginLeft: 'auto',
                  backgroundColor: config.color,
                  color: '#fff' ,
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '10px'
                }}>
                  {data.invoices.length}
                </span>
              </div>
              <div style={{ padding: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Suma (netto po korektach):</span>
                  <span style={{ fontSize: '13px', fontWeight: 600 }}>{formatMoney(data.total)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--success)' }}>Opłacone:</span>
                  <span style={{ fontSize: '12px', color: 'var(--success)' }}>{formatMoney(data.paid)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--danger)' }}>Do zapłaty:</span>
                  <span style={{ fontSize: '12px', color: 'var(--danger)' }}>{formatMoney(data.unpaid)}</span>
                </div>
                {data.corrections !== 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '4px', borderTop: '1px dashed var(--border)' }}>
                    <span style={{ fontSize: '10px', color: 'var(--purple)' }}>Korekty:</span>
                    <span style={{ fontSize: '11px', color: 'var(--purple)' }}>{formatMoney(data.corrections)}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Harmonogram platnosci */}
      <div style={{
        backgroundColor: "var(--card-bg)",
        borderRadius: '6px',
        border: '1px solid var(--border)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '0.75rem 1rem',
          background: 'linear-gradient(135deg, var(--purple), var(--purple))',
          color: 'white'
        }}>
          <span style={{ fontWeight: 600, fontSize: '13px' }}>{'📅'} Harmonogram płatności</span>
        </div>
        <div style={{ padding: '1rem' }}>
          {(() => {
            const unpaidInvoices = (Array.isArray(allInvoices) ? allInvoices : [])
              .filter(inv => inv.status !== 'zatwierdzona' && inv.status !== 'odrzucona' && inv.data_platnosci)
              .sort((a, b) => new Date(a.data_platnosci) - new Date(b.data_platnosci));

            if (unpaidInvoices.length === 0) {
              return (
                <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>
                  {'✅'} Brak zaplanowanych płatności
                </div>
              );
            }

            return unpaidInvoices.slice(0, 15).map(inv => {
              const overdue = isOverdue(inv);
              const days = getDaysUntilDue(inv.data_platnosci);

              return (
                <div
                  key={inv.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px',
                    backgroundColor: overdue ? 'var(--danger-light)' : 'var(--bg)',
                    borderRadius: '4px',
                    marginBottom: '6px',
                    border: `1px solid ${overdue ? 'var(--danger-light)' : 'var(--border)'}`
                  }}
                >
                  <div
                    onClick={() => navigate(`/purchase-invoices/${inv.id}`)}
                    style={{ cursor: 'pointer', flex: 1 }}
                  >
                    <div style={{ fontWeight: 500, fontSize: '12px' }}>{inv.numer_faktury}</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{inv.dostawca_nazwa}</div>
                  </div>
                  <div style={{ textAlign: 'center', flex: 1 }}>
                    <div style={{ fontSize: '11px', color: overdue ? 'var(--danger)' : 'var(--text-secondary)' }}>
                      {formatDate(inv.data_platnosci)}
                    </div>
                    <div style={{ fontSize: '10px', color: overdue ? 'var(--danger)' : 'var(--text-muted)' }}>
                      {overdue ? `${Math.abs(days)} dni po terminie` : `za ${days} dni`}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', marginRight: '10px' }}>
                    <div style={{ fontWeight: 600, fontSize: '12px', color: overdue ? 'var(--danger)' : 'var(--text-secondary)' }}>
                      {formatMoney(inv.suma_brutto)}
                    </div>
                    {inv.sposob_platnosci && (
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                        {paymentTypeConfig[inv.sposob_platnosci]?.icon} {paymentTypeConfig[inv.sposob_platnosci]?.label || inv.sposob_platnosci}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={(e) => markAsPaid(inv, e)}
                    disabled={markingPaid === inv.id}
                    style={{
                      padding: '4px 10px',
                      background: markingPaid === inv.id ? 'var(--text-muted)' : 'var(--success)',
                      color: '#fff' ,
                      fontSize: '11px',
                      cursor: markingPaid === inv.id ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      whiteSpace: 'nowrap'
                    }}
                    title="Oznacz jako zapłacono"
                  >
                    {markingPaid === inv.id ? '⏳' : '✅'} Zapłacono
                  </button>
                </div>
              );
            });
          })()}
        </div>
      </div>
    </div>
  );
}
